import { Type } from '@angular/core';
import {
  JepToolsMixin,
  queueStyleLayerVisibility,
  ThemeAwareVectorTileLayer,
  ThemeColor
} from '@jeppesen-foreflight/jmkw-types';
import { NGXLogger } from 'ngx-logger';

// Predefined layer regex patterns
// These match keywords anywhere in the layer path after the theme prefix
const LAYER_PATTERNS = ['urban', 'road', 'admin'];

export class CulturalLayer extends JepToolsMixin<
  Type<ThemeAwareVectorTileLayer>,
  {
    toggleVisibility: (
      visible?: boolean,
      params?: { theme?: ThemeColor }
    ) => boolean;
  }
>(ThemeAwareVectorTileLayer, {
  toggleVisibility(
    this: CulturalLayer,
    visible?: boolean,
    params?: { theme?: ThemeColor }
  ): boolean {
    return this.updateLayerVisibility(visible, params);
  }
}) {
  // Optional logger for diagnostic logging
  logger?: NGXLogger;

  // Main method to update layer visibility and styling
  private updateLayerVisibility(
    visible?: boolean,
    params?: { theme?: ThemeColor }
  ): boolean {
    // Explicitly set visibility if provided, otherwise toggle
    if (visible !== undefined) {
      this.visible = visible;
    } else {
      this.visible = !this.visible;
    }

    // Update styling based on theme - now handled automatically by ThemeAwareVectorTileLayer
    if (params?.theme) {
      this.applyTheme(params.theme); // Use the inherited method
    }

    return this.visible;
  }

  // Override the updateVectorStyles method to implement cultural-specific styling
  updateVectorStyles(theme: ThemeColor, visible?: boolean): void {
    const allStyleLayers = this.currentStyleInfo?.style.layers || [];

    this.logger?.debug(
      `[CulturalLayer] updateVectorStyles called - theme: ${theme}, visible: ${visible}, total style layers: ${allStyleLayers.length}`
    );

    // Log all available style layers (only first time or when explicitly debugging)
    if (allStyleLayers.length > 0) {
      this.logger?.trace(
        `[CulturalLayer] All available style layer IDs:`,
        allStyleLayers.map((l: { id: string }) => l.id)
      );
    }

    const matchedLayers: string[] = [];
    const unmatchedLayers: string[] = [];

    LAYER_PATTERNS.forEach((pattern) => {
      const currentThemeRegex = this.createThemeRegex(pattern, theme);
      const oppositeThemeRegex = this.createThemeRegex(
        pattern,
        theme === 'dark' ? 'light' : 'dark'
      );

      this.logger?.debug(
        `[CulturalLayer] Pattern: "${pattern}" - Current theme regex: ${currentThemeRegex}, Opposite theme regex: ${oppositeThemeRegex}`
      );

      // Update layer visibility in current style
      allStyleLayers.forEach((layer: { id: string }) => {
        if (currentThemeRegex.test(layer.id)) {
          matchedLayers.push(layer.id);
          queueStyleLayerVisibility(
            this,
            layer.id,
            visible ? 'visible' : 'none'
          );
          this.logger?.trace(
            `[CulturalLayer] Setting layer "${layer.id}" visibility to: ${visible ? 'visible' : 'none'}`
          );
        } else if (oppositeThemeRegex.test(layer.id)) {
          matchedLayers.push(layer.id);
          queueStyleLayerVisibility(this, layer.id, 'none');
          this.logger?.trace(
            `[CulturalLayer] Hiding opposite theme layer: "${layer.id}"`
          );
        }
      });
    });

    // Identify layers that don't match any pattern
    allStyleLayers.forEach((layer: { id: string }) => {
      if (!matchedLayers.includes(layer.id)) {
        unmatchedLayers.push(layer.id);
      }
    });

    if (unmatchedLayers.length > 0) {
      this.logger?.debug(
        `[CulturalLayer] ${unmatchedLayers.length} style layers did NOT match any pattern:`,
        unmatchedLayers
      );
    }

    this.logger?.debug(
      `[CulturalLayer] Pattern matching complete - Matched: ${matchedLayers.length}, Unmatched: ${unmatchedLayers.length}`
    );
  }

  // Create regex for themed layer identification
  // Pattern: ^{theme}/.*{keyword}
  // Example: ^Light/.*admin matches Light/point/admin/region/...
  private createThemeRegex(
    pattern: string,
    theme: ThemeColor = 'light'
  ): RegExp {
    const themePrefix = theme === 'dark' ? 'Dark' : 'Light';
    return new RegExp(`^${themePrefix}/.*${pattern}`);
  }
}
